import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, Alert, Chip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, Skeleton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Tabs, Tab,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Layout from '../components/Layout';
import {
  fetchSupplierSummaries, fetchSupplierDetail,
  SupplierSummary, SupplierDetail, apiErrorText,
  fetchSupplierContacts, saveSupplierContact } from '../api/client';

function fmtEUR(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function fmtDate(d?: string | null): string {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('de-DE'); } catch { return d; }
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toFixed(1) + '%';
}

type SortKey =
  | 'name' | 'invoice_count' | 'article_count' | 'total_spend_eur'
  | 'last_invoice_date' | 'open_alerts' | 'avg_overcharge_pct';
type SortDir = 'asc' | 'desc';

export default function LieferantenPage() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState<SupplierSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [sortKey, setSortKey] = useState<SortKey>('total_spend_eur');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Detail dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [detail, setDetail] = useState<SupplierDetail | null>(null);
  const [contacts, setContacts] = useState<Record<string, string>>({});
  const [phoneDraft, setPhoneDraft] = useState('');
  useEffect(() => { fetchSupplierContacts().then(setContacts).catch(() => {}); }, []);
  useEffect(() => { setPhoneDraft(detail ? (contacts[detail.name] || '') : ''); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [detail]);
  const savePhone = async () => {
    if (!detail) return;
    const digits = phoneDraft.replace(/\D/g, '');
    await saveSupplierContact(detail.name, digits);
    setContacts(prev => ({ ...prev, [detail.name]: digits }));
  };
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSupplierSummaries(dateFrom || undefined, dateTo || undefined);
      setSuppliers(data);
    } catch (e: any) {
      setError(apiErrorText(e) || 'Fehler beim Laden der Lieferanten');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { refresh(); }, [refresh]);

  const sortedSuppliers = useMemo(() => {
    const copy = [...suppliers];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av);
      const bs = String(bv);
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return copy;
  }, [suppliers, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const openDetail = async (name: string) => {
    setDlgOpen(true);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    setDetailTab(0);
    try {
      const d = await fetchSupplierDetail(name);
      setDetail(d);
    } catch (e: any) {
      setDetailError(apiErrorText(e) || 'Fehler beim Laden der Details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDlgOpen(false);
    setDetail(null);
    setDetailError(null);
  };

  const renderSortLabel = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sortKey === key ? sortDir : false}>
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'asc'}
        onClick={() => handleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Lieferanten
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Übersicht aller Lieferanten mit Ausgaben und Preiswarnungen.
        </Typography>
      </Box>

      {/* Date-range filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <TextField
              label="Von"
              type="date"
              size="small"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Bis"
              type="date"
              size="small"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={refresh}
              disabled={loading}
            >
              Filter anwenden
            </Button>
            {(dateFrom || dateTo) && (
              <Button
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                disabled={loading}
              >
                Zurücksetzen
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Skeleton variant="rounded" height={200} />
      ) : suppliers.length === 0 ? (
        <Alert severity="info">Keine Lieferanten im gewählten Zeitraum gefunden.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {renderSortLabel('name', 'Lieferant')}
                {renderSortLabel('invoice_count', '# Rechnungen', 'right')}
                {renderSortLabel('article_count', '# Artikel', 'right')}
                {renderSortLabel('total_spend_eur', 'Gesamtausgaben', 'right')}
                {renderSortLabel('last_invoice_date', 'Letzte Rechnung')}
                {renderSortLabel('open_alerts', 'Offene Warnungen', 'right')}
                {renderSortLabel('avg_overcharge_pct', 'Ø Überzahlung', 'right')}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSuppliers.map((s) => (
                <TableRow
                  key={s.name}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openDetail(s.name)}
                >
                  <TableCell><strong>{s.name}</strong></TableCell>
                  <TableCell align="right">{s.invoice_count}</TableCell>
                  <TableCell align="right">{s.article_count}</TableCell>
                  <TableCell align="right">{fmtEUR(s.total_spend_eur)}</TableCell>
                  <TableCell>{fmtDate(s.last_invoice_date)}</TableCell>
                  <TableCell align="right">
                    {s.open_alerts > 0
                      ? <Chip size="small" color="warning" label={s.open_alerts} />
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {s.avg_overcharge_pct != null && s.avg_overcharge_pct > 0
                      ? <Typography variant="body2" color="error.main">{fmtPct(s.avg_overcharge_pct)}</Typography>
                      : fmtPct(s.avg_overcharge_pct)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail dialog */}
      <Dialog open={dlgOpen} onClose={closeDetail} fullWidth maxWidth="md">
        <DialogTitle>
          {detail?.name || 'Lieferant'}
        </DialogTitle>
        {detail && (
          <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small" label="WhatsApp-Nummer (international, z. B. 4915112345678)"
              value={phoneDraft} onChange={(e) => setPhoneDraft(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <Button size="small" variant="outlined" onClick={savePhone}
              disabled={phoneDraft.replace(/\D/g, '') === (contacts[detail.name] || '')}>
              Speichern
            </Button>
          </Box>
        )}
        <DialogContent>
          {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
          {detailLoading || !detail ? (
            <Skeleton variant="rounded" height={200} />
          ) : (
            <>
              {/* KPI cards */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2, mb: 3,
              }}>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">Rechnungen</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {detail.invoice_count}
                  </Typography>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">Artikel</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {detail.article_count}
                  </Typography>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">Ausgaben gesamt</Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {fmtEUR(detail.total_spend_eur)}
                  </Typography>
                </CardContent></Card>
                <Card variant="outlined"><CardContent>
                  <Typography variant="caption" color="text.secondary">Offene Warnungen</Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={detail.open_alerts ? 'error.main' : 'success.main'}
                  >
                    {detail.open_alerts}
                  </Typography>
                </CardContent></Card>
              </Box>

              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
                <Tab label={`Top Artikel (${detail.top_articles.length})`} />
                <Tab label={`Letzte Rechnungen (${detail.recent_invoices.length})`} />
              </Tabs>

              {detailTab === 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Art-Nr</TableCell>
                        <TableCell>Bezeichnung</TableCell>
                        <TableCell align="right">Letzter Preis</TableCell>
                        <TableCell align="right">Gesamtausgaben</TableCell>
                        <TableCell align="right">Anzahl Käufe</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.top_articles.map((a) => (
                        <TableRow
                          key={a.article_no}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            const params = new URLSearchParams({
                              q: a.article_no,
                              suppliers: detail.name,
                            });
                            navigate(`/artikel?${params.toString()}`);
                          }}
                        >
                          <TableCell>{a.article_no}</TableCell>
                          <TableCell>{a.name}</TableCell>
                          <TableCell align="right">{fmtEUR(a.last_unit_price)}</TableCell>
                          <TableCell align="right">{fmtEUR(a.total_spend)}</TableCell>
                          <TableCell align="right">{a.line_count}</TableCell>
                        </TableRow>
                      ))}
                      {detail.top_articles.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography variant="body2" color="text.secondary" align="center">
                              Keine Artikel gefunden.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {detailTab === 1 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Datum</TableCell>
                        <TableCell>Rechnung-Nr</TableCell>
                        <TableCell align="right">Positionen</TableCell>
                        <TableCell align="right">Summe netto</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.recent_invoices.map((inv) => (
                        <TableRow key={inv.id} hover>
                          <TableCell>{fmtDate(inv.invoice_date)}</TableCell>
                          <TableCell>{inv.invoice_no || '—'}</TableCell>
                          <TableCell align="right">{inv.line_count}</TableCell>
                          <TableCell align="right">{fmtEUR(inv.total_net)}</TableCell>
                        </TableRow>
                      ))}
                      {detail.recent_invoices.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography variant="body2" color="text.secondary" align="center">
                              Keine Rechnungen gefunden.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
